import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/src/components/MdxLayout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1>{`Rules`}</h1>
    <h2>{`The spirit of the competition`}</h2>
    <p>{`Remember that hackathons are like marathons.
Some people go to compete but most people take part to better themselves and have fun.
Whatever the reason you're at a hackathon, make sure you're upholding the `}<a parentName="p" {...{
        "href": "https://medium.com/@tfogo/the-spirit-of-hackathons-a0d81a65060a#.6cx5ac9t8"
      }}>{`hacker spirit`}</a>{` by collaborating with other teams, helping beginners, and having fun.`}</p>
    <h2>{`The rules of the competition`}</h2>
    <ol>
      <li parentName="ol">{`A team should consist of a minimum of 1 person and a max of 4.
There will be up to 4 prizes distributed per team.
You are free to choose your teammates either prior to or on the day of the event.
You are also free to work on your own.`}</li>
      <li parentName="ol">{`Teams should be made up exclusively of high school or undergraduate students who are not organizers, volunteers, judges, sponsors, or in any other privileged position at the event.`}</li>
      <li parentName="ol">{`All team members should be present at the event and in communication with the rest of the team.`}</li>
      <li parentName="ol">{`Teams are welcome to solicit gain advice and support from organizers, volunteers, sponsors, and others.`}</li>
      <li parentName="ol">{`All work on a project should be done at the hackathon.`}</li>
      <li parentName="ol">{`Teams can use an idea they had before the event.`}</li>
      <li parentName="ol">{`Teams can work on ideas that have already been done.
Hacks do not have to be “innovative”.
If a team wants to work on a common idea they are allowed to do so and will be judged on the quality of their hack.
Truly original ideas are hard to find and teams might not know an idea has been done before anyway.`}</li>
      <li parentName="ol">{`Teams can work on an idea that they have worked on before provided they do not re-use code or other project materials.`}</li>
      <li parentName="ol">{`Teams can use libraries, frameworks, or open-source code in their projects.
Working on a project before the event and open-sourcing it for the sole purpose of using the code during the event is against the spirit of the rules and is not allowed.`}</li>
      <li parentName="ol">{`Adding new features to existing projects is allowed.
Judges will only consider new functionality introduced or new features added during the hackathon in determining the winners.`}</li>
      <li parentName="ol">{`Teams must stop hacking once the time is up.
However, teams are allowed to debug and make small fixes to their programs after time is up.
e.g. If during demoing your hack you find a bug that breaks your application and the fix is only a few lines of code, it's okay to fix that.
Making large changes or adding new features is not allowed.`}</li>
      <li parentName="ol">{`Projects that violate the `}<a parentName="li" {...{
          "href": "https://mlh.io/code-of-conduct"
        }}>{`Code of Conduct`}</a>{` are not allowed.`}</li>
      <li parentName="ol">{`Teams can be disqualified from the competition at the organizers' discretion.
Reasons might include but are not limited to breaking the Rules of the Competition, breaking the `}<a parentName="li" {...{
          "href": "https://mlh.io/code-of-conduct"
        }}>{`Code of Conduct`}</a>{`, or other unsporting behaviour.`}</li>
      <li parentName="ol">{`To receive any awarded prizes and/or swag, you must have submitted an application, and been accepted, in the `}<a parentName="li" {...{
          "href": "https://apply.wafflehacks.org"
        }}>{`WaffleHacks Application Portal`}</a>{`.
Submitting a project on Devpost is `}<strong parentName="li">{`NOT`}</strong>{` sufficient. `}</li>
    </ol>
    <h2>{`Demos`}</h2>
    <p>{`After hacking finishes, teams will show their projects each other and to the judges by submitting on `}<a parentName="p" {...{
        "href": "https://wffl.link/devpost"
      }}>{`Devpost`}</a>{`.`}</p>
    <p>{`You are strongly encouraged to present a demo of what you have built.
Pitches or presentations are discouraged.
You are not judged on the quality of your pitch or the quality of your idea.
As you are judged on what you built, you'll only hurt yourself by not showing a demo.`}</p>
    <p>{`You are encouraged to present what you have done even if your hack is broken or you weren’t able to finish.
It's okay if you didn't finish your hack—that happens all the time!
Completion is only one part of the judging criteria, so you might still do well.
Also, demoing is not just about the competition.
It's a chance to share with others what you learned and what you tried to build—that's what hacking's all about!
In the case that you don't have anything to demo, you can give a presentation about what you tried and what you learned.`}</p>
    <h2>{`Judging Criteria`}</h2>
    <p>{`Teams will be judged on these four criteria.
Judges will weigh the criteria equally.
During judging, participants should try to describe what they did for each criterion in their project.`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Technology:`}</strong>
        <ul parentName="li">
          <li parentName="ul">{`How technically impressive was the hack?`}</li>
          <li parentName="ul">{`Was the technical problem the team tackled difficult?`}</li>
          <li parentName="ul">{`Did it use a particularly clever technique or did it use many different components?`}</li>
          <li parentName="ul">{`Did the technology involved make you go "Wow"?`}</li>
        </ul>
      </li>
      <li parentName="ul"><strong parentName="li">{`Design:`}</strong>
        <ul parentName="li">
          <li parentName="ul">{`Did the team put thought into the user experience?`}</li>
          <li parentName="ul">{`How well designed is the interface?`}
            <ul parentName="li">
              <li parentName="ul">{`For a website, this might be about how beautiful the CSS or graphics are.`}</li>
              <li parentName="ul">{`For a hardware project, it might be more about how good the human-computer interaction is.`}</li>
            </ul>
          </li>
          <li parentName="ul">{`Is it easy to use or does it use a cool interface?`}</li>
        </ul>
      </li>
      <li parentName="ul"><strong parentName="li">{`Originality:`}</strong>
        <ul parentName="li">
          <li parentName="ul">{`Can your hack inspire innovation in the next generation of students?`}</li>
          <li parentName="ul">{`Does it do something entirely novel, or at least take a fresh approach to an old problem?`}</li>
          <li parentName="ul">{`This category will be based solely on the idea behind your project, regardless of functionality`}</li>
        </ul>
      </li>
      <li parentName="ul"><strong parentName="li">{`Impact/Usefulness:`}</strong>
        <ul parentName="li">
          <li parentName="ul">{`Does your hack have a social impact?`}</li>
          <li parentName="ul">{`Does it fulfill a real need people have?`}</li>
          <li parentName="ul">{`This category will be based solely on the idea behind your project, regardless of functionality`}</li>
        </ul>
      </li>
      <li parentName="ul"><strong parentName="li">{`Feasibility/Practicality:`}</strong>
        <ul parentName="li">
          <li parentName="ul">{`Is this project feasible and practical in the next couple years?`}</li>
          <li parentName="ul">{`Can your project scale to meet the requirements of mass adoption?`}</li>
        </ul>
      </li>
    </ul>
    <p>{`These criteria will guide judges but ultimately judges are free to make decisions based on their gut feeling of which projects are the most impressive and most deserving.`}</p>
    <p>{`It's important to note that these judging criteria do not include:`}</p>
    <ul>
      <li parentName="ul">{`How good your code is.
It doesn't matter if your code is messy, or not well commented, or uses inefficient algorithms.
Hacking is about playing around, making mistakes, and learning new things.
If your code isn't production ready, we're not going to mark you down.`}</li>
      <li parentName="ul">{`How well you pitch.
Hacking is about building and learning, not about selling.`}</li>
      <li parentName="ul">{`How good the idea is.
Again, hackathons aren't about coming up with innovative ideas.
It's about building and learning.`}</li>
    </ul>
    <p>{`So don't worry about coming up with the next big idea or building the next Facebook. You'll have plenty of time for that outside the hackathon. just focus on learning, having fun, and making new friends. At the end of the day the skills you learn and the friends you make might lead to the next big thing—but you don't have to do that to win a hackathon.`}</p>
    <h2>{`Remember!`}</h2>
    <p>{`The competition is just a part of the hackathon.
To make the most out of the event, try something new, teach other people, and make new friends!`}</p>
    <p><strong parentName="p">{`Happy Hacking from the WaffleHacks team!`}</strong></p>
    <h6><em parentName="h6">{`Last Updated: May 17, 2022`}</em></h6>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      